import clsx from "clsx";
import React, { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "wouter/use-location";

import { useLanguage } from "../../../i18n/useLanguage";
import { useBuildComparison } from "../../../queries/useBuildComparison";
import { createHashStateHook } from "../../../stores/useHashState";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { LinearProgress } from "../../../ui/LinearProgress";
import { Description } from "../Common/Description";
import { TirePartDetails } from "../Common/TirePartDetails";
import { Warranty, hasWarranty } from "../Common/Warranty";
import { AddToQuote } from "./AddToQuote";
import { AdditionalCosts } from "./AdditionalCosts";
import { GrandTotal } from "./GrandTotal";
import { OtherInformtation, hasOtherInfo } from "./OtherInformtation";
import { PricingDetails } from "./PricingDetails";
import { ProductInfo } from "./ProductInfo";
import { Promotion, hasPropmotions } from "./Promotion";
import { SeasonInfo } from "./SeasonInfo";
import { Subtotal } from "./Subtotal";
import { Tax } from "./Tax";

export const compareTiresHash = "#compare-tires";
export const useCompareHash = createHashStateHook(compareTiresHash);

const rowCaptionClass = "font-medium min-w-[8rem] print:min-w-[6rem] pr-4";
export const gridCellClass = "min-w-[12rem] pr-4";

type TireComparisonGridProps = {
  compareIds: string[];
  printMode: boolean;
};

export const TireComparisonGrid: FunctionComponent<TireComparisonGridProps> = ({
  compareIds,
  printMode,
}) => {
  const search = useSearch();
  const queryParams = new URLSearchParams(search);

  const { t } = useTranslation();
  const [language] = useLanguage();
  const { data, isLoading } = useBuildComparison(compareIds, {
    keepPreviousData: true,
  });

  return (
    <section
      className={clsx(
        "space-y-2",
        printMode ? "text-xs overflow-hidden" : "overflow-x-auto",
      )}
      id="product-info-section"
    >
      {/* Product info */}
      {(!data || isLoading) && <LinearProgress className="h-0.5" />}

      {data?.map((items, index) => {
        const isFront = index % 2 === 0;
        const isStaggered = data.length > 1;
        const isLastChunk = index === data.length - 1;

        const quantity = isStaggered ? 2 : 4;

        const gridTemplateHeaderColumns = `1fr ${items
          .map(() => "2fr")
          .join(" ")}`;
        const gridColumn = `span ${items.length || 1}`;
        const gridTemplateColumns = `${items.map(() => "1fr").join(" ")}`;

        return (
          <Fragment key={index}>
            <header
              className={clsx("grid gap-2 items-end", index > 0 && "pt-8")}
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <div>
                <h3 className={clsx("md:text-[1.16em]", rowCaptionClass)}>
                  {t("compare.productInformation")}:
                </h3>
              </div>
              {items.map((item) => {
                return (
                  <div key={item.part.partDetails.primaryPart.itemId}>
                    <ProductInfo part={item.part} />
                  </div>
                );
              })}
            </header>
            <hr className="my-[0.05rem]" />
            {/* Product details */}
            <div
              className="grid gap-2 items-center"
              id="product-details-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>
                {t("compare.productDetails")}:
              </h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map((item) => {
                  return (
                    <Fragment key={item.part.partDetails.primaryPart.itemId}>
                      <TirePartDetails
                        className={gridCellClass}
                        isFront={isStaggered && isFront}
                        isRear={isStaggered && !isFront}
                        part={item.part.partDetails.primaryPart}
                      />
                    </Fragment>
                  );
                })}
              </div>
            </div>
            {/* Season info */}
            <div
              className="grid gap-2 items-center"
              id="season-info-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.season")}:</h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map((item) => (
                  <SeasonInfo
                    key={item.part.partDetails.primaryPart.itemId}
                    part={item.part}
                  />
                ))}
              </div>
            </div>
            {/* Description */}
            <div
              className="grid print:hidden gap-2 items-center"
              id="description-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.description")}:</h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map((item) => (
                  <Description
                    className={gridCellClass}
                    key={item.part.partDetails.primaryPart.itemId}
                    part={item.part}
                  />
                ))}
              </div>
            </div>
            {/* Warranty */}
            {hasWarranty(items) && (
              <div
                className="grid print:hidden gap-2 items-center"
                id="warranty-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <h4 className={rowCaptionClass}>{t("compare.warranty")}:</h4>
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {items.map((item) => (
                    <Warranty
                      className={gridCellClass}
                      key={item.part.partDetails.primaryPart.itemId}
                      part={item.part}
                    />
                  ))}
                </div>
              </div>
            )}
            {/* Other Information */}
            {hasOtherInfo(items) && (
              <div
                className="grid print:hidden gap-2 items-center"
                id="other-information-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <h4 className={rowCaptionClass}>
                  {t("compare.otherInformation")}:
                </h4>
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {items.map((item) => (
                    <OtherInformtation
                      key={item.part.partDetails.primaryPart.itemId}
                      part={item.part}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Pricing Information */}
            <section
              className="grid gap-2 mt-8 print:hidden"
              id="pricing-header-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h3 className={clsx("md:text-[1.16em]", rowCaptionClass)}>
                {t("compare.pricingInformation")}:
              </h3>
            </section>
            <hr className="my-1 print:hidden" />

            {/* Pricing Details */}
            <div
              className="grid gap-2 items-center"
              id="pricing-details-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>
                {t("compare.pricingDetails")}:
              </h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map((item) => (
                  <PricingDetails
                    isFront={isFront}
                    isStaggered={isStaggered}
                    key={item.part.partDetails.primaryPart.itemId}
                    part={item.part}
                    quantity={quantity}
                  />
                ))}
              </div>
            </div>

            {/* Promotion */}
            {hasPropmotions(items, quantity, language) && (
              <div
                className="grid gap-2 items-center"
                id="promotion-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <h4 className={rowCaptionClass}>{t("compare.promotion")}:</h4>
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {items.map((item) => (
                    <Promotion
                      key={item.part.partDetails.primaryPart.itemId}
                      part={item.part}
                      quantity={quantity}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Additional Costs */}
            <div
              className="grid gap-2 items-center"
              id="additional-costs-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>
                {t("compare.additionalCosts")}:
              </h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map((item) => (
                  <AdditionalCosts
                    key={item.part.partDetails.primaryPart.itemId}
                    part={item.part}
                    pricing={item.partQuantity}
                    quantity={quantity}
                  />
                ))}
              </div>
            </div>

            {/* Subtotal */}
            <div
              className="grid gap-2 items-center mt-2"
              id="subtotal-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.subtotal")}:</h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map(({ part, partQuantity }) => (
                  <Subtotal
                    key={part.partDetails.primaryPart.itemId}
                    part={part}
                    pricing={partQuantity}
                    quantity={quantity}
                  />
                ))}
              </div>
            </div>

            {/* Tax */}
            <div
              className="grid gap-2 items-center mt-2"
              id="tax-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.tax")}:</h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map(({ part, partQuantity }) => (
                  <Tax
                    key={part.partDetails.primaryPart.itemId}
                    part={part}
                    pricing={partQuantity}
                    quantity={quantity}
                  />
                ))}
              </div>
            </div>

            {/* Grand Total */}
            <div
              className="grid gap-2 items-center mt-2"
              id="grand-total-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.grandTotal")}:</h4>
              <div
                className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                style={{ gridColumn, gridTemplateColumns }}
              >
                {items.map(({ part, partQuantity }) => (
                  <GrandTotal
                    key={part.partDetails.primaryPart.itemId}
                    part={part}
                    pricing={partQuantity}
                    quantity={quantity}
                  />
                ))}
              </div>
            </div>

            {/* Add to Quote */}
            {!printMode && (
              <div
                className="grid gap-2 items-center mt-2"
                id="add-to-quote-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <span className={rowCaptionClass}></span>
                <div
                  className="grid gap-2 p-4 pl-0 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {items.map(({ part }) => (
                    <AddToQuote
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                      quantity={quantity}
                    />
                  ))}
                </div>
              </div>
            )}

            {printMode && isLastChunk && (
              <>
                <section
                  className="grid gap-2 mt-8 print:hidden"
                  id="pricing-header-section"
                  style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
                >
                  <h3 className={clsx("md:text-[1.16em]", rowCaptionClass)}>
                    {t("compare.clientInformation")}:
                  </h3>
                </section>
                <hr className="my-1 print:hidden" />
                <section
                  className="grid gap-2 items-center mt-2"
                  id="add-to-quote-section"
                  style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
                >
                  <span className={rowCaptionClass} />
                  <div
                    className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                    style={{ gridColumn, gridTemplateColumns }}
                  >
                    <KeyValue>
                      <>{t("leadReport.customerName")}:</>
                      <>
                        {[
                          queryParams?.get("customerFirstName"),
                          queryParams?.get("customerLastName"),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </>
                    </KeyValue>
                    <KeyValue>
                      <>{t("leadReport.customerEmail")}:</>
                      {queryParams?.get("customerEmail")}
                    </KeyValue>
                    <KeyValue>
                      <>{t("leadReport.customerPhone")}:</>
                      {queryParams?.get("customerPhone")}
                    </KeyValue>
                  </div>
                </section>
              </>
            )}
          </Fragment>
        );
      })}

      {data && <div id="tw-print-ready" />}
    </section>
  );
};

/**
 * @see docs/worklog/TW-866-compare-promo/rfc.md
 * @see docs/worklog/TW-866-compare-promo/tfc.md
 */
